import { useState } from 'react'

import { Avatar, Box, Divider, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add, Pencil } from '@factorialco/f0-react/icons/app'
import { JoinExternalIntegrationFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { ExternalProfilesModal } from './ExternalProfilesModal'
import { IndeedConfigurationModal } from './IndeedConfigurationModal'
import { useUpdateIntegrationProfile } from './useUpdateIntegration'

interface Props {
  integrationProfile: JoinExternalIntegrationFragment
  onUpdate: () => void
}

export const ExternalProfilesItem = ({ integrationProfile, onUpdate }: Props) => {
  const { updateIntegrationProfile } = useUpdateIntegrationProfile({ integrationProfile })
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const isIndeed = integrationProfile.name === 'Indeed'

  const isEnabled = integrationProfile.status === 'enabled'
  const hasConfiguration = isIndeed || integrationProfile.configuration.length > 0
  const profileConfigured = integrationProfile.configuration.every((option) => !!option.value)
  // TODO: quick fix for indeed, remove this when this configuration is available in the JOIN's API.
  // Indeed is a special case, doesn't need to be configured..
  const connected = isIndeed ? isEnabled : profileConfigured && isEnabled

  const handleUpdate = async (deactivate = false) => {
    const result = await updateIntegrationProfile(deactivate)

    result?.success && onUpdate()
  }

  const handleOnUpdated = () => {
    setIsModalOpen(false)
    onUpdate()
  }

  return (
    <>
      <Box
        gap="s8"
        borderRadius={{
          all: 'abs016',
        }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        paddingY="s12"
      >
        <Box paddingX="s16">
          <Avatar label={integrationProfile.slug} url={integrationProfile.logoUrl} />
        </Box>
        <Box gap="s12">
          <Box gap="s4" paddingX="s16">
            <Text.Normal color="grey1000" size="300" weight="semibold">
              {integrationProfile.name}
            </Text.Normal>
            <Text.Normal size="200" ellipsis lineClamp={2}>
              {integrationProfile.description}
            </Text.Normal>
          </Box>
          <>
            <Divider />
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceBetween"
              paddingX="s16"
              minHeight="s32"
            >
              <Tag
                inline
                showCircle
                leftItem="circle"
                type={connected ? 'correct' : 'neutral'}
                size="small"
              >
                {connected
                  ? i18n.t('ats.settings.job_promotions.external_profiles.connected')
                  : i18n.t('ats.settings.job_promotions.external_profiles.disconnected')}
              </Tag>

              <Box flexDirection="row" gap="s8">
                {!connected ? (
                  <F0Button
                    icon={Add}
                    variant="outline"
                    label={i18n.t('common.add')}
                    onClick={() =>
                      hasConfiguration && !isIndeed ? setIsModalOpen(true) : handleUpdate()
                    }
                  />
                ) : (
                  <F0Button
                    variant="outline"
                    label={i18n.t('common.remove')}
                    onClick={() => handleUpdate(true)}
                  />
                )}

                {connected && hasConfiguration && (
                  <F0Button
                    icon={Pencil}
                    label={i18n.t('common.edit')}
                    onClick={() => setIsModalOpen(true)}
                  />
                )}
              </Box>
            </Box>
          </>
        </Box>
      </Box>
      {isModalOpen && hasConfiguration && !isIndeed && (
        <ExternalProfilesModal
          integrationProfile={integrationProfile}
          onUpdated={handleOnUpdated}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      {/* // TODO: quick fix, remove this when this configuration is available in the JOIN's API */}
      {isModalOpen && isIndeed && (
        <IndeedConfigurationModal
          integrationProfile={integrationProfile}
          onUpdated={handleOnUpdated}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}
